import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import utils, {
  getHasElementsInArray,
  getUserFullName,
  normalizeEmail,
} from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import { FilterOption } from 'models/Filters';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import {
  qaRoleTagLabelMap,
  roleTagLabelMap,
} from 'router/subrouters/DataRoom/constants';
import { getDistinctOptions } from 'router/subrouters/DataRoom/utils';
import { TEAM_FILTER_PARAMS } from '../data';

// Filters for the name and the two role columns. The team role is left out on
// purpose: a member can hold several, same reason it is not sortable.
export default function useTeamMemberFilters(members: DataRoomMember[] = []) {
  const { t } = useTranslation();

  const { params } = useQueryParams<Record<string, string>>();

  const {
    [TEAM_FILTER_PARAMS.NAME]: nameParam,
    [TEAM_FILTER_PARAMS.FILES_ROLE]: filesRoleParam,
    [TEAM_FILTER_PARAMS.QA_ROLE]: qaRoleParam,
  } = params;

  const filterOptions = useMemo<FilterOption[]>(
    () => [
      {
        value: TEAM_FILTER_PARAMS.NAME,
        label: t('name'),
        isMultiple: true,
        isSearchable: true,
        // Invited members have no name yet. The table labels them "Invited",
        // but a list of identical options is impossible to pick from, so the
        // filter identifies them by email instead.
        options: members.map(
          ({ _id, isAccepted, firstName, lastName, email }) => ({
            value: _id,
            label:
              (isAccepted
                ? getUserFullName(firstName, lastName)
                : normalizeEmail(email)) || t('invited'),
          }),
        ),
      },
      {
        value: TEAM_FILTER_PARAMS.FILES_ROLE,
        label: t('filesRole'),
        isMultiple: true,
        isSearchable: false,
        options: getDistinctOptions(
          members.map(({ role }) => role),
          (role) => t(roleTagLabelMap[role] ?? role),
        ),
      },
      {
        value: TEAM_FILTER_PARAMS.QA_ROLE,
        label: t('qaRole'),
        isMultiple: true,
        isSearchable: false,
        options: getDistinctOptions(
          members.map(({ qaRole }) => qaRole),
          (qaRole) => t(qaRoleTagLabelMap[qaRole] ?? qaRole),
        ),
      },
    ],
    [members, t],
  );

  const filteredMembers = useMemo(() => {
    const names = utils.mapParamsToArray(nameParam);
    const filesRoles = utils.mapParamsToArray(filesRoleParam);
    const qaRoles = utils.mapParamsToArray(qaRoleParam);

    return members.filter(({ _id, role, qaRole }) => {
      if (getHasElementsInArray(names) && !names.includes(_id)) return false;

      if (getHasElementsInArray(filesRoles) && !filesRoles.includes(role))
        return false;

      if (getHasElementsInArray(qaRoles) && !qaRoles.includes(qaRole))
        return false;

      return true;
    });
  }, [filesRoleParam, members, nameParam, qaRoleParam]);

  return { filterOptions, filteredMembers };
}
